import { message as notification } from "antd";
import { resolveProductScan } from "../../../utils/productBarcodes";
import React, { useEffect, useRef } from "react";
import {
  BarcodeFormat,
  BrowserMultiFormatReader,
  DecodeHintType,
  NotFoundException,
} from "@zxing/library";

const formats = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.AZTEC,
  BarcodeFormat.PDF_417,
  BarcodeFormat.MAXICODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.CODABAR,
];

const qrFormats = [BarcodeFormat.QR_CODE];

const QRcomponent = ({ cameraID, onScan, qrOnly = false }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const onScanRef = useRef(onScan);
  const lastResultRef = useRef({ text: "", at: 0 });

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, qrOnly ? qrFormats : formats);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatReader(hints, 350);
    readerRef.current = reader;

    let active = true;
    let resolving = false;
    const scanCallback = async (result, error) => {
      if (result) {
        if (!active || resolving) return;
        const text = result.getText();
        const now = Date.now();
        const lastResult = lastResultRef.current;

        if (lastResult.text === text && now - lastResult.at < 1500) {
          return;
        }

        lastResultRef.current = { text, at: now };
        resolving = true;
        try {
          const resolved = await resolveProductScan(text);
          if (active) onScanRef.current?.({ text: resolved, rawText: text });
        } catch (error) {
          if (active) notification.error("Unable to resolve product barcode. Check connection or barcode number.");
        } finally {
          resolving = false;
        }
        return;
      }

      if (error && !(error instanceof NotFoundException)) {
        const message = String(error?.message || error || "");
        if (
          !message.includes("ResizeObserver loop limit exceeded") &&
          !message.includes(
            "ResizeObserver loop completed with undelivered notifications"
          )
        ) {
          console.error(error);
        }
      }
    };

    const getVideoConstraints = () => {
      const video = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 30, max: 30 },
      };

      if (cameraID) {
        video.deviceId = { exact: cameraID };
      } else {
        video.facingMode = { ideal: "environment" };
      }

      video.advanced = [
        { focusMode: "continuous" },
        { exposureMode: "continuous" },
      ];

      return { audio: false, video };
    };

    const startScanner = async () => {
      if (!videoRef.current) {
        return;
      }

      try {
        await reader.decodeFromConstraints(
          getVideoConstraints(),
          videoRef.current,
          scanCallback
        );
      } catch (error) {
        try {
          await reader.decodeFromVideoDevice(
            cameraID || null,
            videoRef.current,
            scanCallback
          );
        } catch (fallbackError) {
          const message = String(fallbackError?.message || fallbackError || "");
          if (
            !message.includes("ResizeObserver loop limit exceeded") &&
            !message.includes(
              "ResizeObserver loop completed with undelivered notifications"
            )
          ) {
            console.error(fallbackError);
          }
        }
      }
    };

    startScanner();

    return () => {
      active = false;
      reader.reset();
      readerRef.current = null;
    };
  }, [cameraID, qrOnly]);

  return (
    <div
      style={{
        width: "100%",
        aspectRatio: "4 / 3",
        position: "relative",
        overflow: "hidden",
        background: "#000",
      }}
    >
      <video
        ref={videoRef}
        muted
        playsInline
        disablePictureInPicture
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
};

export default QRcomponent;
